<template>
  <form class="filter-dropdown">
    <div class="filter-dropdown__header">
      <p class="filter-dropdown__title text-heading-2xl">
        <span v-text="$string('general.filter_and_sort.filters')" />

        <span
          v-if="nonHiddenActiveFiltersCount"
          class="filter-dropdown__count text-body-s-regular"
          v-text="$string('general.filter_and_sort.applied', {
            replace: {
              num: nonHiddenActiveFiltersCount,
            },
          })"
        />
      </p>

      <btn
        v-if="nonHiddenActiveFiltersCount"
        class="filter-dropdown__clear-all"
        class-label="text-body-s-underline"
        :label="$string('general.filter_and_sort.clear_all')"
        modifiers="text"
        type="button"
        @click.prevent="clearAll"
      />
    </div>

    <template
      v-for="(filter) of filteredFilters"
      :key="filter.id"
    >
      <fieldset
        v-if="!isTypeVendor(filter)"
        class="filter-dropdown__filter"
      >
        <legend
          class="filter-dropdown__filter-legend visually-hidden"
          v-text="filter.label"
        />

        <button
          class="filter-dropdown__filter-button button button--text button--block"
          :class="{
            [$class('active')]: isOpened(filter.id),
          }"
          type="button"
          @click.prevent="toggleFilterDropdown(filter.id)"
        >
          <span class="filter-dropdown__filter-button-label">
            <span
              class="text-body-s-bold"
              v-text="filter.label"
            />

            <span
              v-if="isActiveFilter(filter)"
              class="filter-dropdown__count text-body-s-regular"
              v-text="getActiveFilterText(filter)"
            />
          </span>

          <icon-chevron-down />
        </button>

        <button
          v-if="isActiveFilter(filter)"
          class="filter-dropdown__filter-clear button button--text"
          type="button"
          @click.prevent="clearFilter(filter)"
        >
          <span
            class="text-body-s-underline"
            v-text="$string('general.filter_and_sort.clear')"
          />
        </button>

        <transition-height>
          <div
            v-if="isOpened(filter.id)"
            class="filter-dropdown__dropdown"
          >
            <ul
              v-if="isFilterList(filter)"
              class="filter-dropdown__list"
            >
              <template
                v-for="(value) of filter.values"
                :key="value.id"
              >
                <li
                  v-if="value.count"
                  class="filter-dropdown__list-item"
                >
                  <filter-list-value
                    :active-filters="activeFilters"
                    :name="filter.id"
                    :value="value"
                    @handle-filter="handleFilter"
                  />
                </li>
              </template>
            </ul>

            <range-slider
              v-else-if="isFilterPriceRange(filter)"
              :id="priceArgs.id"
              v-model="priceArgs.value"
              :class="[
                $class('active'),
                {
                  'is-filtered': isActiveFilter(filter),
                },
              ]"
              :config="priceArgs.config"
              is-currency
              :label="priceArgs.label"
              @update-value="handlePriceFilter"
            >
              <template #tooltipText="{ value }">
                <span v-text="$formatMoney(value * 100, $variable('store.moneyFormat'), true)" />
              </template>
            </range-slider>
          </div>
        </transition-height>
      </fieldset>
    </template>
  </form>
</template>

<script>

/**
 * Vue: Filter dropdown (filter-dropdown)
 * -----------------------------------------------------------------------------
 * Filter dropdown used for collection and search pages.
 *
 * @param {Object} activeFilters - Active filters.
 * @param {Object} filters - Filters array.
 * @param {Boolean} [isVehicleFinderCollection] - Collection is either the
 * redirect or results collection.
 * @param {Object} priceRange - Price range.
 * @param {Boolean} [showingVehicleResults] - Showing vehicle finder results.
 *
 * @emits clearAll - Emit clear all.
 * @emits clearFilter - Emit clear filter.
 * @emits handleFilter - Emit filter update.
 * @emits handlePriceFilter - Emit price filter update.
 *
 */
import { mapGetters } from 'vuex'

import Btn from '~global/btn/btn'
import FilterListValue from './filter-list-value'
import RangeSlider from '~async/utils/range-slider/range-slider'
import TransitionHeight from '~async/transition-height/transition-height'

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg'

import { toggleObjectFromArray } from '~/helpers/filters'

export default {
  name: 'FilterDropdown',

  components: {
    Btn,
    FilterListValue,
    IconChevronDown,
    RangeSlider,
    TransitionHeight,
  },

  props: {
    activeFilters: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Array,
      default: () => [],
    },
    isVehicleFinderCollection: {
      type: Boolean,
      default: false,
    },
    priceRange: {
      type: Object,
      default: () => ({}),
    },
    showingVehicleResults: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'clear-all': (payload) => typeof payload === 'object',
    'clear-filter': (payload) => typeof payload === 'object',
    'handle-filter': (payload) => typeof payload === 'object',
    'handle-price-filter': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      openedFilters: [],
      priceArgs: {
        config: {
          max: this.priceRange?.max || 100,
          min: this.priceRange?.min || 0,
          step: 1,
        },
        id: {
          max: 'filter-price-max',
          min: 'filter-price-min',
        },
        label: {
          max: this.$string('general.filter_and_sort.price_max'),
          min: this.$string('general.filter_and_sort.price_min'),
        },
        value: {
          max: this.priceRange?.value?.max || 100,
          min: this.priceRange?.value?.min || 0,
        },
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeVehicle: 'garage/getActiveVehicle',
    }),

    /**
     * Compute filtered list of filters to hide active vehicle filters.
     * @returns {Array}
     */
    filteredFilters() {
      return this.filters.filter((filter) => {
        const valuesHaveProducts = filter.values.some((value) => value.count)

        if (!this.showingVehicleResults) {
          return valuesHaveProducts
        }

        const matchesVehicleFilter = this.activeVehicle?.filters.find((vehicleFilter) => {
          const namespace = vehicleFilter.productMetafield.namespace
          const key = vehicleFilter.productMetafield.key
          const id = `filter.p.m.${namespace}.${key}`

          return id === filter.id
        })

        return !matchesVehicleFilter && valuesHaveProducts
      })
    },

    /**
     * Compute hidden active filters.
     * @returns {Array}
     */
    hiddenActiveFilters() {

      /**
       * Filter out filters that are hidden by theme setting.
       */
      const hiddenByThemeSetting = this.activeFilters.filter((activeFilter) => {
        let parameter = false

        if (activeFilter.productMetafield) {
          parameter = `filter.p.m.${activeFilter.productMetafield.namespace}.${activeFilter.productMetafield.key}`
        } else if (activeFilter.productVendor) {
          parameter = `filter.p.vendor`
        }

        const hiddenFilters = this.isVehicleFinderCollection
          ? this.$variable('vehicleFinder.hiddenVehicleFinderFilters')
          : this.$variable('vehicleFinder.hiddenFilters')

        return hiddenFilters.includes(parameter)
      })

      /**
       * Filter out filters that don't exist in filters.
       * - Means they've been hidden due to active vehicle filters.
       * - Ignore non-product metafield filters.
       */
      const hiddenByActiveVehicle = this.activeFilters.filter((activeFilter) => {
        if (!activeFilter.productMetafield) {
          return false
        }

        const parameter = `filter.p.m.${activeFilter.productMetafield.namespace}.${activeFilter.productMetafield.key}`

        const existsInFilters = this.filteredFilters.find((filteredFilter) => {
          return parameter === filteredFilter.id
        })

        return !existsInFilters
      })

      /**
       * Combine arrays and de-duplicate.
       */
      const merged = [
        ...hiddenByThemeSetting,
        ...hiddenByActiveVehicle,
      ]

      return merged.filter((filter, index) => merged.indexOf(filter) === index)
    },

    /**
     * Compute non-hidden active filters count.
     * @returns {Number}
     */
    nonHiddenActiveFiltersCount() {
      return this.activeFilters.length - this.hiddenActiveFilters.length
    },
  },

  watch: {

    /**
     * Update min/max value in range-slider after priceRange updated.
     */
    priceRange: {
      handler() {
        this.priceArgs.value = {
          max: this.priceRange?.value?.max || 100,
          min: this.priceRange?.value?.min || 0,
        }
      },
      deep: true,
    },
  },

  methods: {

    /**
     * Reset filters and close filter drawer.
     */
    clearAll() {
      this.$emit('clear-all', this.hiddenActiveFilters)
    },

    /**
     * Handle clear filter.
     * @param {String} filter - Filter group.
     */
    clearFilter(filter) {
      this.$emit('clear-filter', filter)
    },

    /**
     * Handle filter (de)selection.
     * @param {Object} filter - Filter object.
     */
    handleFilter(filter) {
      this.$emit('handle-filter', filter)
    },

    /**
     * Handle price filter change.
     * @param {Array} value - Price range.
     */
    handlePriceFilter(value) {
      this.$emit('handle-price-filter', value)
    },

    /**
     * Toggle filter dropdown.
     * @param {String} filterId - Filter Id.
     */
    toggleFilterDropdown(filterId) {
      toggleObjectFromArray(this.openedFilters, filterId)
    },

    /**
     * Get array of active filter inputs.
     * @param {Object} filter - Filter.
     * @returns {Array}
     */
    getActiveFilterInputs(filter) {
      return filter.values.filter((value) => {
        return this.activeFilters.some((input) => {
          return JSON.stringify(value.input) === JSON.stringify(input)
        })
      })
    },

    /**
     * Get active filter text.
     * @param {Object} filter - Filter.
     * @returns {String}
     */
    getActiveFilterText(filter) {
      if (filter.type === 'PRICE_RANGE') {
        return this.$string('general.filter_and_sort.applied', {
          replace: {
            num: '',
          },
        })
      }

      const activeFilterInputs = this.getActiveFilterInputs(filter)
      return this.$string('general.filter_and_sort.applied', {
        replace: {
          num: activeFilterInputs.length,
        },
      })
    },

    /**
     * Check if filter group has active options.
     * @param {Object} filter - Filter.
     * @returns {Boolean}
     */
    isActiveFilter(filter) {
      const priceFilter = this.activeFilters.find((input) => input.price)
      if (filter.type === 'PRICE_RANGE' && priceFilter) {
        return true
      }

      const activeFilterInputs = this.getActiveFilterInputs(filter)
      return activeFilterInputs.length > 0
    },

    /**
     * Check if filter group is opened.
     * @param {String} filterId - Filter Id.
     * @returns {Boolean} - True if filter group is opened.
     */
    isOpened(filterId) {
      return this.openedFilters.includes(filterId)
    },

    /**
     * Return if the filter is a vendor list type.
     * @param {Object} filter - Filter group.
     * @returns {Boolean}
     */
    isTypeVendor(filter) {
      return filter.id === 'filter.p.vendor'
    },

    /**
     * Return if the filter is a (valid) list type.
     * @param {Object} filter - Filter group.
     * @returns {Boolean}
     */
    isFilterList(filter) {
      return filter.type === 'LIST' && filter.id !== 'filter.p.vendor'
    },

    /**
     * Return if the filter is a (valid) price range type.
     * @param {Object} filter - Filter group.
     * @returns {Boolean}
     */
    isFilterPriceRange(filter) {
      return filter.type === 'PRICE_RANGE' && this.priceRange.value
    },
  },
}
</script>

<style lang="scss">
@import './filter-dropdown';
</style>
